/**
 * menu-dnd — współdzielony stan i pomocniki dla przeciągania (drag & drop)
 * elementów z palety kreatora menu na płótno (ADR-0031).
 *
 * Dodawanie z palety używa NATYWNEGO drag & drop przeglądarki (zero dodatkowych
 * bibliotek). Zmiana kolejności istniejących pozycji dalej należy do @dnd-kit
 * wewnątrz `MenuCanvas` — te dwa mechanizmy nie kolidują (różne systemy zdarzeń:
 * @dnd-kit słucha pointer-eventów na uchwycie, paleta emituje natywne drag-eventy).
 *
 * Przeciągana pozycja nie przechodzi przez `dataTransfer` (tam trzymamy tylko
 * token typu MIME) — obiekt `MenuItem` trzymamy w module-singletonie, bo w całej
 * aplikacji trwa najwyżej jedno przeciąganie naraz. Paleta ustawia go na
 * `dragstart`, płótno odczytuje na `drop`, `dragend` czyści.
 */

import type { AdminMenuItem, MenuItem } from '@saas/shared';

type AnyMenuItem = MenuItem | AdminMenuItem;

/** Typ MIME ustawiany na dataTransfer — rozpoznaje przeciąganie z palety (nie plik/tekst). */
export const PALETTE_DND_MIME = 'application/x-menu-palette-item';

let pendingDragItem: AnyMenuItem | null = null;

/** Paleta: zapamiętaj pozycję (z gotowym UUID) rozpoczynającą przeciąganie. */
export function beginPaletteDrag(item: AnyMenuItem): void {
  pendingDragItem = item;
}

/** Koniec/anulowanie przeciągania — zawsze czyść (dragend). */
export function endPaletteDrag(): void {
  pendingDragItem = null;
}

/** Płótno: pobierz przeciąganą pozycję (na drop). Nie czyści — to robi dragend. */
export function getPaletteDragItem(): AnyMenuItem | null {
  return pendingDragItem;
}

/** Czy trwa przeciąganie z palety (płótno używa do warunkowego preventDefault). */
export function hasPaletteDrag(): boolean {
  return pendingDragItem !== null;
}

/**
 * Tożsamość pozycji do oznaczenia „już w menu" w palecie. `null` = brak tożsamości
 * (sekcja/separator — zawsze można dodać, nie oznaczamy). Custom link identyfikujemy
 * po URL-u (dotyczy też pozycji funkcjonalnych, które paleta wstawia jako custom_link).
 */
export function paletteItemKey(
  item: Pick<AnyMenuItem, 'type'> & {
    moduleCode?: string;
    itemKey?: string;
    areaCode?: string;
    url?: string;
  },
): string | null {
  switch (item.type) {
    case 'module':
      return item.moduleCode ? `module:${item.moduleCode}` : null;
    case 'system_item':
      return item.itemKey ? `system:${item.itemKey}` : null;
    case 'installed_area':
      return item.areaCode ? `area:${item.areaCode}` : null;
    case 'custom_link':
      return item.url ? `link:${item.url}` : null;
    default:
      return null;
  }
}

/**
 * Liczy indeks wstawienia w kontenerze na podstawie pozycji kursora (Y) i geometrii
 * wierszy: pozycja ląduje PRZED pierwszym wierszem, którego środek jest poniżej kursora;
 * jeśli kursor jest poniżej środka wszystkich — na koniec. Czysta funkcja (bez DOM), żeby
 * dała się przetestować bez layoutu jsdom.
 */
export function computeInsertIndex(
  rects: ReadonlyArray<{ top: number; height: number }>,
  clientY: number,
): number {
  for (let i = 0; i < rects.length; i++) {
    const r = rects[i]!;
    if (clientY < r.top + r.height / 2) return i;
  }
  return rects.length;
}

/** Zbiera tożsamości wszystkich pozycji obecnych w hierarchii (rekurencyjnie). */
export function collectUsedPaletteKeys(items: AnyMenuItem[]): Set<string> {
  const set = new Set<string>();
  const walk = (arr: AnyMenuItem[]) => {
    for (const it of arr) {
      const key = paletteItemKey(it);
      if (key) set.add(key);
      if (it.children && it.children.length > 0) walk(it.children as AnyMenuItem[]);
    }
  };
  walk(items);
  return set;
}
